import { useState, useCallback, ChangeEvent, KeyboardEvent } from "react";
import { processEnvServer } from "hooks/useSsrHooks";
import React, { FC } from "react";
import sanitizeHtml from "sanitize-html";
import { createStyles, makeStyles } from "@mui/styles";
import { getReqContext } from "@RHCommerceDev/utils/reqContext";
import { getSelectorsByUserAgent } from "react-device-detect";

import { useTheme, InputBaseProps } from "@mui/material";
import { useCookiesWithPermission } from "hooks/useCookiesWithPermission";
import { useLocalization } from "hooks/useLocalization";
import {
  FONT_BARON_SANS,
  RH_MEDIUM,
  RH_DARK,
  BCT_PATHS
} from "utils/constants";
import { codePattern } from "utils/regex";
import { formatSearchText } from "utils/formatSearchUrl";
import yn from "yn";
import { useEnv } from "hooks/useEnv";
import { useHistory } from "react-router";
import { useSetSipId } from "@RHCommerceDev/hooks/atoms/useSIPID";
import useSite from "hooks/useSite";
import { useAppId } from "hooks/useAppId";
import RHRSearchIconV2 from "@RHCommerceDev/icon-search-rhr-v2";
import { useHeaderAtom } from "hooks/atoms/useHeaderAtom";
import parseUrlPath from "@RHCommerceDev/utils/parse-url-path";
import useIsoRedirect from "@RHCommerceDev/hooks/useIsoRedirect";

export const useStyles = isDarkMode =>
  makeStyles(() =>
    createStyles({
      searchInput: {
        padding: "8px !important",
        marginLeft: "12px !important",
        paddingLeft: "32px !important",
        fontFamily: `${FONT_BARON_SANS} !important`,
        color: `${isDarkMode ? RH_MEDIUM : RH_DARK} !important`
      }
    })
  );

interface HeaderSearchTextFieldProps extends InputBaseProps {
  isDarkMode?: boolean;
  isHeaderSearch?: boolean;
  isHighlighted?: boolean;
  openDrawer: () => void;
  handleMenuClose?: () => void;
}

const HeaderSearchTextField: FC<
  HeaderSearchTextFieldProps & { color?: string }
> = ({ isHeaderSearch, isDarkMode, color = undefined, isHighlighted }) => {
  const siteId = useSite();
  const history = useHistory();
  const theme = useTheme();
  const prefix = useLocalization();
  const [searchText, setSearchText] = useState<string>("");
  const [showTypeAhead, setShowTypeAhead] = useState(false);
  const { setStorageValueWrapper } = useCookiesWithPermission();
  const env = useEnv();
  const {
    state,
    setSelectedCategoryIndex,
    closeCategoryDropdown,
    closeHamburgerDropdown
  } = useHeaderAtom();
  const req = getReqContext();
  let mobile = false;
  const userAgentSSR = req && req?.headers["user-agent"];
  if (userAgentSSR) {
    const { isMobile } = getSelectorsByUserAgent(userAgentSSR);
    mobile = !!isMobile;
  }
  const setSipId = useSetSipId();
  const { isConcierge } = useAppId();
  const FEATURE_NEW_HEADER = yn(env?.FEATURE_NEW_HEADER);
  const FEATURE_SHOP_UI = yn(env?.FEATURE_SHOP_UI);

  const [isOpen, setIsOpen] = useState(false);

  const handleTypeahead = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!processEnvServer) {
        setStorageValueWrapper({
          storageKey: "analytics-access",
          value: "typeahead"
        });
      }
      setSearchText(event.target.value);
      if (!showTypeAhead && !isHeaderSearch) {
        setShowTypeAhead(true);
      }
    },
    [showTypeAhead, isHeaderSearch, setStorageValueWrapper]
  );

  const handleEnterPressed = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const bctPath =
        yn(env?.FEATURE_BCT_SUNSET) && !isConcierge
          ? BCT_PATHS[siteId] || ""
          : "";
      const searchPath = yn(env?.FEATURE_URL_CHANGE)
        ? formatSearchText(searchText)
        : `results.jsp?Ntt=${encodeURIComponent(
            searchText?.trim()
          )}&Ns=product.sale%7C1`;
      if (
        !!searchText &&
        event.key === "Enter" &&
        !!codePattern.test(searchText)
      ) {
        setSipId(null);

        if (!processEnvServer) {
          sessionStorage.removeItem("pdpViewItem");
          sessionStorage.removeItem("rhrGridChoice");
          setStorageValueWrapper({
            storageKey: "analytics-access",
            value: "direct"
          });
          setStorageValueWrapper({
            storageKey: "analytics-searchbar-enter",
            value: searchText
          });
        }
        const path = sanitizeHtml(`${prefix}${bctPath}/search/${searchPath}`);

        if (FEATURE_SHOP_UI) {
          useIsoRedirect(parseUrlPath(path), "windowAssign", {
            history
          });
        } else {
          history.push(path);
        }

        setSelectedCategoryIndex(-1);
        if (state.isCategoryDropdownOpen) {
          closeCategoryDropdown();
        }
        if (state.isHamburgerDropdownOpen) {
          closeHamburgerDropdown();
        }
        setSearchText("");
      }
    },

    [searchText, prefix, setStorageValueWrapper]
  );

  return FEATURE_NEW_HEADER ? (
    <div
      className="h-6.5 flex w-[150px] items-center gap-1.5 rounded-3xl border-[0.5px] p-1 lg:w-[194px]"
      style={{
        borderColor:
          color ||
          (isDarkMode ? theme.palette.common.white : theme.palette.common.black)
      }}
    >
      <RHRSearchIconV2
        style={{
          color:
            color ||
            (isDarkMode
              ? theme.palette.common.white
              : theme.palette.common.black)
        }}
        data-testid="search-icon"
      />
      <input
        autoFocus
        value={searchText}
        className="w-full bg-transparent font-rhsans text-[13px] font-normal leading-[1.5em] tracking-[0.02em] caret-rh-dark outline-none sm:w-[120px] lg:w-[140px]"
        style={{
          color:
            color ||
            (isDarkMode
              ? theme.palette.common.white
              : theme.palette.common.black)
        }}
        onChange={handleTypeahead}
        onKeyUp={handleEnterPressed}
        placeholder={"Search"}
      />
    </div>
  ) : (
    <div className="flex items-center gap-[6px]">
      <button className="p-0" onClick={() => setIsOpen(prev => !prev)}>
        <RHRSearchIconV2
          style={{
            color:
              color ||
              (isDarkMode
                ? theme.palette.common.white
                : theme.palette.common.black)
          }}
          data-testid="search-icon"
        />
      </button>
      {isOpen && (
        <input
          autoFocus
          value={searchText}
          className="rounded-none border-b-[0.75px] border-b-rh-dark bg-transparent font-rhsans text-[13px] font-normal leading-[150%] tracking-[0.195px] caret-rh-dark outline-none sm:w-[120px] lg:w-[140px]"
          style={{
            color:
              color ||
              (isDarkMode
                ? theme.palette.common.white
                : theme.palette.common.black)
          }}
          onChange={handleTypeahead}
          onKeyUp={handleEnterPressed}
        />
      )}
    </div>
  );
};

export default HeaderSearchTextField;
